import { Loader2Icon, XIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { Label } from '@/components/ui/label.js';
import { useCurrentWorkspaceRole } from '@/features/workspace/useCurrentWorkspaceRole.js';

import type {
  ConnectionProfilePatch,
  ConnectorConnection,
} from '../../services/integration-api.js';

import {
  ConnectionDetailsFields,
  type ConnectionDetailsValue,
} from './ConnectionDetailsFields.js';
import {
  labelOriginNote,
  usageHintOriginNote,
} from './connection-value-origin.js';

/**
 * The owner's "members may use this connection in Quality" choice. It renders only for the owner of
 * `workspaceId`, and it mounts only inside the open dialog, so the role is read only when asked for.
 */
function QualitySharingField({
  workspaceId,
  connectionId,
  providerName,
  checked,
  disabled,
  onCheckedChange,
}: {
  workspaceId: string;
  connectionId: string;
  providerName: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}): JSX.Element | null {
  const workspaceRole = useCurrentWorkspaceRole(workspaceId, true);
  if (workspaceRole !== 'owner') return null;
  const checkboxId = `connection-settings-${connectionId}-quality`;
  return (
    <div className="mt-4 flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(next) => onCheckedChange(next === true)}
        />
        <Label htmlFor={checkboxId} className="cursor-pointer font-normal">
          Let members use this connection in Quality
        </Label>
      </div>
      <p className="text-caption leading-relaxed text-muted-foreground">
        Members who have not connected their own {providerName} account can
        choose to run Quality through this connection. Their runs act as you.
        This applies in every workspace you own. A run never deletes anything.
      </p>
    </div>
  );
}

/**
 * Settings of one live connection: what it is called, when the agent should reach for it, and, for a
 * workspace owner, whether members may run Quality through it.
 *
 * Where a rename LANDS is the thing this dialog has to be honest about. In a working workspace it writes
 * that workspace's profile and nowhere else; somebody who is not told that believes they renamed the
 * connection everywhere, and the next workspace they open contradicts them. In their personal space the
 * same control writes the connection's own values, and says so instead.
 *
 * Only the fields that actually moved are sent — the service reads the body as a subset, so a rename
 * that also shipped an untouched instruction would be this dialog answering a question nobody asked.
 */
export function ConnectionSettingsDialog({
  open,
  onOpenChange,
  providerName,
  connection,
  personalWorkspace,
  qualitySharingWorkspaceId = null,
  busy = false,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerName: string;
  connection: ConnectorConnection;
  /** Whether the surrounding workspace is the caller's own personal one. */
  personalWorkspace: boolean;
  /** The shared workspace whose owner is asked whether members may use this connection in Quality. */
  qualitySharingWorkspaceId?: string | null;
  busy?: boolean;
  onSave: (patch: ConnectionProfilePatch) => void | Promise<void>;
}): JSX.Element {
  const seeded: ConnectionDetailsValue = {
    label: connection.label,
    usageHint: connection.usageHint ?? '',
  };
  const [value, setValue] = useState<ConnectionDetailsValue>(seeded);
  const [backgroundUse, setBackgroundUse] = useState(connection.backgroundUse);

  /** Re-seeds from the server's current answer on every open, never from the last edit left behind. */
  useEffect(() => {
    if (!open) return;
    setValue({
      label: connection.label,
      usageHint: connection.usageHint ?? '',
    });
    setBackgroundUse(connection.backgroundUse);
  }, [open, connection.label, connection.usageHint, connection.backgroundUse]);

  const scopeNote = personalWorkspace
    ? 'This is the connection’s own description, used in every workspace that has nothing more specific to say.'
    : 'The name and the instruction apply in this workspace only. Your other workspaces keep what they already say about this connection.';

  /**
   * Where the values on screen came from, said only in a WORKING workspace. In the caller's personal
   * space the fields hold the connection's own values by definition — there is nothing above them to
   * inherit from — so an "inherited" marker there would describe a relationship that does not exist.
   */
  const labelNote = personalWorkspace
    ? null
    : labelOriginNote(connection.labelFrom);
  const usageHintNote = personalWorkspace
    ? null
    : usageHintOriginNote(connection.usageHintFrom);

  /** The subset that moved. An untouched field is absent, which is how the service leaves it alone. */
  const buildPatch = (): ConnectionProfilePatch => {
    const patch: ConnectionProfilePatch = {};
    if (value.label.trim() !== connection.label) {
      patch.label = value.label.trim();
    }
    if (value.usageHint.trim() !== (connection.usageHint ?? '')) {
      patch.usageHint = value.usageHint.trim();
    }
    if (backgroundUse !== connection.backgroundUse) {
      patch.backgroundUse = backgroundUse;
    }
    return patch;
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) return;
        onOpenChange(next);
      }}
      title={`${providerName} connection settings`}
      description={`Change what this ${providerName} connection is called and when the agent should use it.`}
      titleHidden
      showCloseButton={false}
      className="flex max-h-[85vh] w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-6 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {providerName} connection settings
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={busy}
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </div>

        {!personalWorkspace && (
          <p className="mt-2 text-compact leading-relaxed text-muted-foreground">
            Changes here apply in this workspace only.
          </p>
        )}

        {connection.usageHint === null && (
          <p className="mt-2 text-caption leading-relaxed text-muted-foreground">
            This connection has no instruction yet, so the agent has nothing to
            go on when it has to choose between your connections.
          </p>
        )}

        <div className="mt-4">
          <ConnectionDetailsFields
            idPrefix={`connection-settings-${connection.connectionId}`}
            providerName={providerName}
            value={value}
            onChange={setValue}
            scopeNote={scopeNote}
            labelNote={labelNote}
            usageHintNote={usageHintNote}
            disabled={busy}
          />
        </div>

        {qualitySharingWorkspaceId && (
          <QualitySharingField
            workspaceId={qualitySharingWorkspaceId}
            connectionId={connection.connectionId}
            providerName={providerName}
            checked={backgroundUse}
            disabled={busy}
            onCheckedChange={setBackgroundUse}
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={busy}
          onClick={() => void onSave(buildPatch())}
        >
          {busy && (
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          )}
          Save
        </Button>
      </div>
    </AppDialog>
  );
}
